import React, { useEffect, useState } from "react";
import {
  BookOpen, Brain, Calculator, ChartBarBig, Check, ChevronDown, ChevronRight, CircleSlash,
  CornerUpLeft, Database, Lightbulb, MessageCircleQuestion, Redo2, Table2, Circle, CircleX,
  ArrowRight, GitBranch, LoaderCircle,
} from "lucide-react";
import { DEFAULT_DECIMALS, figureKey, formatDuration, roundCell, roundFigures } from "./journalModel";

// The block vocabulary, one component per kind. Every string is TEXT (React escapes it);
// nothing here ever sets innerHTML.

export const VERB_ICONS = {
  read: BookOpen,
  opened: BookOpen,
  queried: Database,
  calculated: Calculator,
  checked: Check,
  asked: MessageCircleQuestion,
};

function Disclosure({ label, children, mono = false, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="wp-disc">
      <button type="button" className="wp-disc-btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        {label}
      </button>
      {open && <div className={`wp-disc-body${mono ? " wp-mono" : ""}`}>{children}</div>}
    </div>
  );
}

// Text that arrives while the run is live is revealed a few characters at a time, so a
// paragraph that landed whole reads as being written rather than pasted. `stream` false
// (a finished journal, or a block that was already there when the panel opened) shows
// the whole text at once. Purely presentational: the model holds the full text throughout.
export function useReveal(text, stream) {
  const full = String(text || "");
  const [shown, setShown] = useState(stream ? 0 : full.length);
  useEffect(() => {
    if (!stream) { setShown(full.length); return undefined; }
    let n = 0;
    setShown(0);
    const id = setInterval(() => {
      n += 6;
      if (n >= full.length) { setShown(full.length); clearInterval(id); } else setShown(n);
    }, 16);
    return () => clearInterval(id);
  }, [full, stream]);
  return full.slice(0, shown);
}

export function ReasoningBlock({ block, stream = false }) {
  const more = Array.isArray(block.more) ? block.more.filter(Boolean) : [];
  const text = useReveal(block.text, stream);
  const revealing = stream && text.length < String(block.text || "").length;
  return (
    <div className={`wp-reason${revealing ? " wp-revealing" : ""}`} data-block-kind="reasoning">
      <Brain size={15} aria-hidden="true" />
      <div>
        <span>{text}{revealing && <span className="wp-caret" aria-hidden="true" />}</span>
        {more.length > 0 && (
          <Disclosure label={`Full reasoning (${more.length} more line${more.length === 1 ? "" : "s"})`}>
            {more.map((line, i) => <p key={i}>{line}</p>)}
          </Disclosure>
        )}
      </div>
    </div>
  );
}

export function ActionRow({ block }) {
  const Icon = VERB_ICONS[block.verb] || BookOpen;
  // A refusal the run recovered from (the backend marks it `recovered`) is a retry, not an
  // error: shown muted with its reason one click away, never in red (product review,
  // 2026-10-07). A refusal never recovered from keeps the refused styling — it explains a gap.
  const retried = block.ok === false && block.recovered === true;
  const refused = block.ok === false && !retried;
  const running = block.state === "running";
  return (
    <div className={`wp-act${refused ? " wp-act-refused" : ""}${retried ? " wp-act-retried" : ""}${running ? " wp-act-running" : ""}`} data-block-kind="action" data-state={running ? "running" : undefined}>
      <div className="wp-act-row">
        {running ? <LoaderCircle size={15} className="animate-spin" aria-hidden="true" />
          : refused ? <CircleX size={15} aria-hidden="true" /> : <Icon size={15} aria-hidden="true" />}
        <span className="wp-act-verb">{block.label}</span>
        {/* A row can now carry no detail at all: with DR_WORK_QUERY_ARGS off the backend
            sends the verb and the purpose, not the call's arguments. Skip the span so the
            flex row does not open a gap where the arguments used to be. */}
        {block.detail && <span className="wp-act-detail" title={block.detail}>{block.detail}</span>}
        {refused && <span className="wp-act-tag">refused</span>}
        {retried && <span className="wp-act-tag">retried</span>}
        {block.attempts > 1 && !refused && <span className="wp-act-tag">{block.attempts} attempts</span>}
        {!refused && !running && block.duration_ms > 0 && (
          <span className="wp-act-dur">{formatDuration(block.duration_ms)}</span>
        )}
        {running && <span className="wp-act-dur wp-act-live">working<span className="wp-caret" aria-hidden="true" /></span>}
      </div>
      {block.purpose && block.purpose !== block.detail && (
        <div className="wp-act-because">
          <CornerUpLeft size={12} aria-hidden="true" />
          <span>{block.purpose}</span>
        </div>
      )}
      {retried && block.reason && (
        <Disclosure label="Why it was retried">
          <p>{block.reason}{block.reason_more ? ` ${block.reason_more}` : ""}</p>
        </Disclosure>
      )}
      {refused && block.reason && (
        <div className="wp-act-reason" data-act-reason="true">
          <CircleX size={12} aria-hidden="true" />
          {/* The lead sentences, and the rest of the refusal folded under them. The data
              layer's refusals are written to be acted on ("either filter it to one value,
              or put it in group_by"), so the tail is worth a click — it used to be cut
              off in Python and lost. */}
          <div className="wp-act-reason-body">
            <span>{block.reason}</span>
            {block.reason_more && (
              <Disclosure label="The rest of the refusal">
                <p>{block.reason_more}</p>
              </Disclosure>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaCell({ cell, scale, decimals = DEFAULT_DECIMALS }) {
  if (cell.v === undefined || cell.v === null || !scale) return <span>{roundCell(cell, decimals)}</span>;
  const width = Math.max(2, Math.round((Math.abs(cell.v) / scale) * 64));
  const dir = cell.dir === "neg" ? "dn" : "dp";
  return (
    <span className={`wp-delta ${dir}`}>
      <i style={{ width: `${width}px` }} />
      <span>{roundCell(cell, decimals)}</span>
    </span>
  );
}

// ── the whole result as a file ─────────────────────────────────────────────────
// The panel shows at most 60 rows; a result with more carries them unformatted
// (`block.download`, bridge/journal.py::_attach_download) and the browser builds the CSV.
export function toCsv(download) {
  const quote = (v) => {
    const text = v === null || v === undefined ? "" : String(v);
    const needsQuotes = ['"', ",", "\n", "\r"].some((mark) => text.includes(mark));
    return needsQuotes ? `"${text.split('"').join('""')}"` : text;
  };
  const lines = [(download.columns || []).map(quote).join(",")];
  (download.rows || []).forEach((row) => lines.push(row.map(quote).join(",")));
  return lines.join("\r\n");
}

function DownloadCsv({ download, total }) {
  if (!download || !(download.rows || []).length) return null;
  const save = () => {
    const blob = new Blob(["\ufeff" + toCsv(download)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${download.name || "result"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <button type="button" className="wp-evi-more wp-evi-download" onClick={save}>
      Download all {Number(total || download.rows.length).toLocaleString("en-US")} rows (CSV)
      {download.truncated ? " — first 5,000" : ""}
    </button>
  );
}

export function EvidenceTable({ block, badge = null, flashKey = "", decimals = DEFAULT_DECIMALS }) {
  const [showAll, setShowAll] = useState(false);
  const rows = block.rows || [];
  const openRows = block.open_rows || 8;
  const shown = showAll ? rows : rows.slice(0, openRows);
  const hidden = rows.length - shown.length;
  const deltaColumns = new Set(block.delta_columns || []);
  const hasGroups = Array.isArray(block.groups) && block.groups.length > 0 && block.groups.some((g) => g.label);
  // A `windows` table is a period comparison with no dimension, transposed: the metrics
  // read down the side and the two windows are the columns. Its first column names each
  // row's metric — the job the group header does in the wide layout — so it is set as a
  // heading rather than as data.
  const windows = block.layout === "windows";
  return (
    <div className={`wp-evi${windows ? " wp-evi-windows" : ""}`} data-block-kind="evidence_table">
      <div className="wp-evi-cap">
        <Table2 size={13} aria-hidden="true" />
        <span>{block.caption}</span>
        {/* The subject row stays highlighted; the "subject pinned" chip read as developer
            jargon to a business reader (product review, 2026-10-07). */}
        {badge}
      </div>
      <div className="wp-evi-scroll">
        <table>
          <thead>
            {hasGroups && (
              <tr>
                {block.groups.map((g, i) => (
                  <th key={i} colSpan={g.span} className={g.label ? "wp-grp" : ""}>{g.label}</th>
                ))}
              </tr>
            )}
            <tr>
              {(block.columns || []).map((c, i) => (
                <th key={i} className={c.align === "num" ? "r" : ""}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, ri) => (
              <tr key={ri} className={row.subject ? "wp-subject" : ""}>
                {(row.cells || []).map((cell, ci) => {
                  const key = figureKey(cell.t);
                  const flash = flashKey && key && key === flashKey;
                  const numeric = (block.columns || [])[ci]?.align === "num";
                  return (
                    // `exact` is the unrounded figure behind an abbreviated cell — 35.02M
                    // shows, 35,017,522 is on hover. The panel is the working, so the
                    // precise value has to stay reachable somewhere.
                    <td key={ci} className={`${numeric ? "r" : ""}${flash ? " wp-flash" : ""}`} data-figure={key || undefined} data-exact={figureKey(cell.exact) || undefined} title={cell.exact || undefined}>
                      {/* A `verbatim` table is quoted as the tool wrote it (1.87 stays 1.87):
                          re-rounding it made the panel disagree with the answer. */}
                      {deltaColumns.has(ci) ? <DeltaCell cell={cell} scale={block.scale} decimals={decimals} />
                        : block.verbatim ? cell.t : roundCell(cell, decimals)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hidden > 0 && (
        <button type="button" className="wp-evi-more" onClick={() => setShowAll(true)}>
          <ChevronDown size={13} aria-hidden="true" />Show all {rows.length} rows
        </button>
      )}
      {showAll && rows.length > openRows && (
        <button type="button" className="wp-evi-more" onClick={() => setShowAll(false)}>
          <ChevronRight size={13} aria-hidden="true" />Show fewer
        </button>
      )}
      {block.download && (
        <div className="wp-evi-dl">
          {showAll && block.total_rows_in_result > rows.length && (
            <span className="wp-evi-dl-note">Showing {rows.length} of {Number(block.total_rows_in_result).toLocaleString("en-US")} rows.</span>
          )}
          <DownloadCsv download={block.download} total={block.total_rows_in_result} />
        </div>
      )}
    </div>
  );
}

// ── a series over time: one line per entity, the biggest moves, the rows behind it ──
//
// The backend sends it for a result grouped by one time level and at most one entity
// (bridge/journal.py::_series_chart): up to eight lines, the three largest rises and falls,
// and the full table. Colours are the validated eight-slot categorical palette, assigned in
// the order the backend sends the series (largest total first), never cycled.
const SERIES_W = 640, SERIES_H = 220, PAD_L = 56, PAD_R = 12, PAD_T = 10, PAD_B = 26;

function compactAxis(value) {
  const a = Math.abs(value);
  if (a >= 1e9) return `${(value / 1e9).toFixed(1)}bn`;
  if (a >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(value / 1e3).toFixed(0)}k`;
  return `${Math.round(value)}`;
}

export function SeriesChart({ block, badge = null, flashKey = "", decimals = DEFAULT_DECIMALS }) {
  const series = block.series || [];
  const [hoverX, setHoverX] = React.useState(null);
  const xs = Array.from(new Set(series.flatMap((s) => (s.points || []).map((p) => p.x)))).sort((a, b) => a - b);
  const values = series.flatMap((s) => (s.points || []).map((p) => p.v));
  if (!xs.length || !values.length) return block.table ? <EvidenceTable block={block.table} flashKey={flashKey} decimals={decimals} /> : null;
  let lo = Math.min(...values), hi = Math.max(...values);
  const span = hi - lo || Math.abs(hi) || 1;
  lo -= span * 0.06; hi += span * 0.06;
  const x0 = xs[0], x1 = xs[xs.length - 1];
  const px = (x) => PAD_L + (x1 === x0 ? 0.5 : (x - x0) / (x1 - x0)) * (SERIES_W - PAD_L - PAD_R);
  const py = (v) => PAD_T + (1 - (v - lo) / (hi - lo)) * (SERIES_H - PAD_T - PAD_B);
  const ticks = [0, 1, 2, 3].map((i) => lo + ((hi - lo) * i) / 3);
  const xTicks = xs.length <= 12 ? xs : xs.filter((_, i) => i % Math.ceil(xs.length / 8) === 0);
  // A series over several years numbers its points in time order and names each one ("31 '25").
  const xName = (x) => (block.x_labels && block.x_labels[String(x)]) || x;
  const onMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    const at = ((event.clientX - box.left) / box.width) * SERIES_W;
    let best = xs[0];
    xs.forEach((x) => { if (Math.abs(px(x) - at) < Math.abs(px(best) - at)) best = x; });
    setHoverX(best);
  };
  const readout = hoverX === null ? [] : series
    .map((s, i) => ({ i, label: s.label, p: (s.points || []).find((p) => p.x === hoverX) }))
    .filter((r) => r.p)
    .sort((a, b) => b.p.v - a.p.v);
  const moves = (title, list) => list && list.length > 0 && (
    <div className="wp-series-moves">
      <div className="wp-series-moves-h">{title}</div>
      {list.map((m, i) => (
        <div key={i} className="wp-series-move">
          <span className="wp-series-move-l" title={m.label}>{m.label}</span>
          <span className="wp-series-move-x">{block.x_label} {m.x}</span>
          <span className={`wp-series-move-v ${m.v < 0 ? "dn" : "up"}`}>{roundFigures(m.text, decimals)}{m.pct ? ` (${m.pct})` : ""}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div className="wp-chart wp-series" data-block-kind="evidence_chart">
      <div className="wp-evi-cap">
        <ChartBarBig size={13} aria-hidden="true" />
        <span>{block.caption}</span>
        {block.metric_label && <span className="wp-chart-metric">{block.metric_label} by {block.x_label}</span>}
        {badge}
      </div>
      {series.length > 1 && (
        <div className="wp-series-legend">
          {series.map((s, i) => (
            <span key={i} className="wp-series-key"><i className={`wp-s${i + 1}`} />{s.label}</span>
          ))}
          {block.more_series > 0 && <span className="wp-series-key wp-series-more">+{block.more_series} more in the table</span>}
        </div>
      )}
      <div className="wp-series-plot">
        <svg viewBox={`0 0 ${SERIES_W} ${SERIES_H}`} role="img" aria-label={`${block.metric_label} by ${block.x_label}`}
             onPointerMove={onMove} onPointerLeave={() => setHoverX(null)}>
          {ticks.map((t, i) => (
            <g key={i}>
              <line className="wp-series-grid" x1={PAD_L} x2={SERIES_W - PAD_R} y1={py(t)} y2={py(t)} />
              <text className="wp-series-tick" x={PAD_L - 6} y={py(t) + 3} textAnchor="end">{compactAxis(t)}</text>
            </g>
          ))}
          {xTicks.map((x) => (
            <text key={x} className="wp-series-tick" x={px(x)} y={SERIES_H - 8} textAnchor="middle">{xName(x)}</text>
          ))}
          {series.map((s, i) => (
            <polyline key={i} className={`wp-series-line wp-s${i + 1}`} fill="none"
                      points={(s.points || []).map((p) => `${px(p.x)},${py(p.v)}`).join(" ")} />
          ))}
          {hoverX !== null && (
            <g>
              <line className="wp-series-cross" x1={px(hoverX)} x2={px(hoverX)} y1={PAD_T} y2={SERIES_H - PAD_B} />
              {readout.map((r) => (
                <circle key={r.i} className={`wp-series-dot wp-s${r.i + 1}`} cx={px(hoverX)} cy={py(r.p.v)} r="4" />
              ))}
            </g>
          )}
        </svg>
        {hoverX !== null && readout.length > 0 && (
          <div className="wp-series-tip" style={{ left: `${(px(hoverX) / SERIES_W) * 100}%` }}>
            <div className="wp-series-tip-h">{block.x_label} {xName(hoverX)}</div>
            {readout.map((r) => (
              <div key={r.i} className="wp-series-tip-r"><i className={`wp-s${r.i + 1}`} /><span>{r.label}</span><b>{roundFigures(r.p.t, decimals)}</b></div>
            ))}
          </div>
        )}
      </div>
      <div className="wp-series-movers">
        {moves("Biggest rises", block.rises)}
        {moves("Biggest falls", block.falls)}
      </div>
      {block.table && (
        <Disclosure label={`Show the rows (${Number(block.table.total_rows_in_result || block.table.total_rows || (block.table.rows || []).length).toLocaleString("en-US")})`}>
          <EvidenceTable block={block.table} flashKey={flashKey} decimals={decimals} />
        </Disclosure>
      )}
    </div>
  );
}

export function DivergingChart({ block, badge = null, flashKey = "", decimals = DEFAULT_DECIMALS }) {
  if (block.chart === "series") return <SeriesChart block={block} badge={badge} flashKey={flashKey} decimals={decimals} />;
  const bars = block.bars || [];
  return (
    <div className="wp-chart" data-block-kind="evidence_chart">
      <div className="wp-evi-cap">
        <ChartBarBig size={13} aria-hidden="true" />
        <span>{block.caption}</span>
        {/* WHAT THE BARS ARE. The caption names the dataset, the cut and the window — not
            the measure — and a query usually asks for several metrics while a diverging
            chart can only draw ONE of them. Without this the reader sees "Value Share Pct,
            Value Share Point Change" on the action line above and twelve unlabelled bars
            below, with nothing saying which of the two was drawn. */}
        {block.metric_label && (
          <span className="wp-chart-metric">{block.metric_label}</span>
        )}
        {badge}
      </div>
      <div className="wp-chart-rows">
        {bars.map((bar, i) => {
          const neg = bar.v < 0;
          return (
            <div key={i} className={`wp-divrow${bar.subject ? " wp-hi" : ""}`}>
              <span className="wp-divlab" title={bar.label}>{bar.label}</span>
              <span className="wp-divneg">{neg && <><i style={{ width: `${Math.max(2, bar.pct)}%` }} /><b>{roundFigures(bar.text, decimals)}</b></>}</span>
              <span className="wp-divaxis" />
              <span className="wp-divpos">{!neg && <><i style={{ width: `${Math.max(2, bar.pct)}%` }} /><b>{roundFigures(bar.text, decimals)}</b></>}</span>
            </div>
          );
        })}
      </div>
      {block.table && (
        <Disclosure label="Show the full table">
          <EvidenceTable block={block.table} flashKey={flashKey} decimals={decimals} />
        </Disclosure>
      )}
    </div>
  );
}

export function CalcResult({ block, flashKey = "", decimals = DEFAULT_DECIMALS }) {
  const pairs = block.pairs || [];
  return (
    <div className="wp-calc" data-block-kind="calc_result">
      {pairs.length > 0 && (
        <div className="wp-kv">
          {pairs.map((pair, i) => {
            const key = figureKey(pair.value);
            const negative = /^[−-]/.test(String(pair.value || "").trim());
            return (
              <React.Fragment key={i}>
                <div className="wp-kv-k">{pair.key}</div>
                <div className={`wp-kv-v${negative ? " wp-loss" : ""}${flashKey && key === flashKey ? " wp-flash" : ""}`} data-figure={key || undefined}>
                  {roundFigures(pair.value, decimals)}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {!pairs.length && block.table && <EvidenceTable block={block.table} flashKey={flashKey} decimals={decimals} />}
      {!pairs.length && !block.table && block.text && <pre className="wp-calc-text">{roundFigures(block.text, decimals)}</pre>}
      {block.note && (
        <div className="wp-act-reason wp-calc-note" data-calc-note="true">
          <CircleX size={12} aria-hidden="true" />
          <span>{block.note}</span>
        </div>
      )}
      {block.code && (
        <Disclosure label="Show calculation" mono>
          <pre className="wp-code">{block.code}</pre>
        </Disclosure>
      )}
    </div>
  );
}

// A note that only says a step did not apply ("Trend: none was asked") — the method
// talking to itself. Shown as one quiet line, never as a Finding (reported by the client
// on exec_873: "Trend not required for status question." sat in the Findings strip).
export function NoteLine({ block }) {
  return (
    <div className="wp-note" data-block-kind="note">
      <span className="wp-note-label">Not asked</span>
      <span className="wp-note-text">{block.text}</span>
    </div>
  );
}

export function FindingCard({ block, onBackToAnswer = null, stream = false }) {
  const text = useReveal(block.text, stream);
  return (
    <div className="wp-finding" data-block-kind="finding">
      <Lightbulb size={15} aria-hidden="true" />
      <div className="wp-finding-body">
        <div className="wp-finding-label">Finding</div>
        <div className="wp-finding-text">{text}</div>
      </div>
      {onBackToAnswer && (
        <button type="button" className="wp-finding-back" onClick={onBackToAnswer} title="Scroll the chat to the sentence this supports">
          <CornerUpLeft size={11} aria-hidden="true" />In answer
        </button>
      )}
    </div>
  );
}

export function DeviationLine({ block }) {
  const Icon = block.state === "adapted" ? Redo2 : block.state === "failed" ? CircleX : block.state === "not_reached" ? Circle : CircleSlash;
  return (
    <div className={`wp-devi wp-devi-${block.state}`} data-block-kind="deviation">
      <Icon size={15} aria-hidden="true" />
      <span>{block.text}</span>
    </div>
  );
}

export function CausalChain({ block, decimals = DEFAULT_DECIMALS }) {
  const nodes = block.nodes || [];
  return (
    <div className="wp-chain" data-block-kind="chain">
      <div className="wp-chain-label">
        <GitBranch size={13} aria-hidden="true" />
        The answer as a picture{block.scope ? ` — ${block.scope}` : ""}
      </div>
      <div className="wp-flow">
        {nodes.map((node, i) => (
          <React.Fragment key={i}>
            <div className={`wp-node wp-${node.tone}`}>
              <span className="wp-node-lab">{node.label}</span>
              <span className="wp-node-big">{roundFigures(node.big, decimals)}</span>
              <span className="wp-node-sm">{roundFigures(node.small, decimals)}</span>
            </div>
            {i < nodes.length - 1 && <ArrowRight size={16} className="wp-arrow" aria-hidden="true" />}
          </React.Fragment>
        ))}
        {block.counterpart && (
          <>
            <ArrowRight size={16} className="wp-arrow wp-arrow-back" aria-hidden="true" />
            <div className={`wp-node wp-${block.counterpart.tone}`}>
              <span className="wp-node-lab">{block.counterpart.label}</span>
              <span className="wp-node-big">{roundFigures(block.counterpart.big, decimals)}</span>
              <span className="wp-node-sm">{roundFigures(block.counterpart.small, decimals)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function StepBadge({ n, onClick }) {
  return (
    <button type="button" className="wp-badge" onClick={onClick} title={`Open step ${n}`}>Step {n}</button>
  );
}
